import * as yup from 'yup'
import { getHistoryDuration } from '../../packages/utilities'
import { ApplicationFormAnswers } from '../components/ApplicationForm/ApplicationFormContext'
import { AttributionDetails } from '../components/ApplicationForm/types'
import { rankedAddressSituationOptions } from '../components/Questions/CardlessAddressHistoryQuestion/AddressSituation/AddressSituationOptions'
import BaseValidator from './BaseValidator'

const AddressHistoryValidator = {
  LengthOfTimeValidator: (): BaseValidator => {
    const schema = yup.object().shape({
      months: yup.number()
        .transform((val) => (Number.isNaN(val) ? undefined : val))
        .typeError('Please enter how long you\'ve been in this situation')
        .max(11, 'Please enter a value between 0 and 11')
        .min(0, 'Please enter a value between 0 and 11'),
      years: yup.number()
        .transform((val) => (Number.isNaN(val) ? undefined : val))
        .typeError('Please enter how long you\'ve been in this situation')
        .max(100, 'Please enter a value between 0 and 100')
        .min(0, 'Please enter a value between 0 and 100')
    }).test(
      'is-optional',
      "Please enter years or months of how long you've lived here",
      ({ months, years }, { createError }) => {
        if (years === undefined && months === undefined) return createError({ path: 'duration', message: 'Please enter years or months of how long you\'ve lived here' })
        return (!(years === undefined && months === undefined))
      }
    )

    return {
      getValidator: () => schema,
      validateState: (state: ApplicationFormAnswers) => (state.addressHistory?.entries
        ? Object.keys(state.addressHistory?.entries).every((key: string) => {
          const entry = state.addressHistory?.entries[key]
          return schema.isValidSync({
            months: entry?.lengthOfTime?.months,
            years: entry?.lengthOfTime?.years
          })
        }) : false)
    }
  },
  AddressDetailsValidator: (): BaseValidator => {
    const schema = yup.object().shape(
      {
        flatNumber: yup.string()
          .max(35)
          .label('Flat number'),
        buildingName: yup.string()
          .max(35)
          .label('Building name'),
        buildingNumber: yup.string()
          .max(10)
          .when(['flatNumber', 'buildingName'], {
            is: (flatNumber: string, buildingName: string) => (flatNumber === '' || flatNumber === undefined) && (buildingName === '' || buildingName === undefined),
            then: (x) => x.required('Please provide us with a building name, number or flat number')
          })
          .label('Building number'),
        street: yup.string().required('Please enter a street')
          .max(100).label('Street'),
        town: yup.string().required('Please enter a town')
          .max(100).label('Town'),
        county: yup.string()
          .max(100).label('County'),
        postcode: yup.string()
          .required('Please enter a postcode')
          .transform((value) => (typeof value === 'string' ? value.replace(/\s/g, '') : value)) // Remove spaces
          .matches(/^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})$/,
            'Please enter a valid UK postcode')
          .max(20)
          .label('Postcode')
      }
    )

    return {
      getValidator: () => (
        schema
      ),
      validateState: (state: ApplicationFormAnswers) => (state.addressHistory?.entries
        ? Object.keys(state.addressHistory?.entries).every((key: string) => {
          const entry = state.addressHistory?.entries[key]
          return schema.isValidSync({
            flatNumber: entry?.addressDetails?.flatNumber,
            buildingName: entry?.addressDetails?.buildingName,
            buildingNumber: entry?.addressDetails?.buildingNumber,
            street: entry?.addressDetails?.street,
            town: entry?.addressDetails?.town,
            county: entry?.addressDetails?.county,
            postcode: entry?.addressDetails?.postcode
          })
        }) : false)
    }
  },
  AddressSituationValidator: ({ source }: Partial<AttributionDetails>): BaseValidator => {
    const schema = yup.object().shape({
      addressSituation: yup.string()
        .oneOf(rankedAddressSituationOptions.map((o) => o.value as string))
        .required()
    })

    return {
      getValidator: () => schema,
      validateState: (state: ApplicationFormAnswers) => (state.addressHistory?.entries
        ? Object.keys(state.addressHistory?.entries).every((key: string) => {
          const entry = state.addressHistory?.entries[key]
          const canIgnoreUnknownPreviousAddressSituations = source === 'freedomfinance'
            || source === 'freedomfinance-t1'
            || source === 'moneysupermarket'
            || source === 'msm'
            || source === 'clearscoreew'
          if (canIgnoreUnknownPreviousAddressSituations && key !== '1' && !entry?.addressSituation) return true
          return schema.isValidSync({
            addressSituation: entry?.addressSituation?.toLowerCase()
          })
        }) : false)
    }
  },
  AddressHistoryLengthValidator: (): BaseValidator => ({
    getValidator: () => yup.object(),
    validateState: (state: ApplicationFormAnswers) => {
      const entries = state.addressHistory?.entries
      if (!entries) {
        return false
      }
      const historyLength = getHistoryDuration(entries)
      return !!((historyLength.years && historyLength.years >= 3))
    }
  })
}

export default AddressHistoryValidator
